import React from 'react';
import { TextInput } from '@mantine/core';

export interface AfaTextInputProps {
  /** Valor actual del input */
  value?: string;
  
  /** Callback cuando cambia el valor */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  
  /** Dato que viene de Editar */
  defaultValue?: string;

  /** Placeholder del input */
  placeholder?: string;
  
  /** Label del campo */
  label?: string;
  
  /** Descripción adicional */
  description?: string;
  
  /** Mensaje de error */
  error?: string;
  
  /** Si es requerido */
  required?: boolean;
  
  /** Si está deshabilitado */
  disabled?: boolean;
  
  /** Tipo de input */
  type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'search' | 'number';
  
  /** Tamaño del input */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Radio de bordes */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Icono izquierdo */
  leftSection?: React.ReactNode;
  
  /** Icono derecho */
  rightSection?: React.ReactNode;
  
  /** Solo lectura */
  readOnly?: boolean;
  
  /** Props adicionales para el TextInput */
  inputProps?: any;
  
  /** Estilos personalizados */
  styles?: any;
  
  /** Clase CSS */
  className?: string;
  
  /** Nombre del input para formularios */
  name?: string;
  
  /** ID del input */
  id?: string;
}

/**
 * Wrapper del TextInput de Mantine con defaults de AFA y estilos del tema
 * 
 * Ejemplos de uso:
 * 
 * // Básico
 * <AfaTextInput
 *   placeholder="Escriu el teu nom"
 *   label="Nom"
 * />
 * 
 * // Con iconos
 * <AfaTextInput
 *   placeholder="correu@exemple.com"
 *   label="Email"
 *   type="email"
 *   leftSection={<IconMail size={16} />}
 * />
 * 
 * // Controlado
 * const [value, setValue] = useState('');
 * <AfaTextInput
 *   value={value}
 *   onChange={(e) => setValue(e.target.value)}
 *   label="Camp controlat"
 * />
 */
export function AfaTextInput({
  value,
  onChange,
  defaultValue, 
  placeholder = 'Escriu aquí...',
  label,
  description,
  error,
  required = false,
  disabled = false,
  type = 'text',
  size = 'sm',
  radius = 'md',
  leftSection,
  rightSection,
  readOnly = false,
  inputProps,
  styles,
  className,
  name,
  id,
}: AfaTextInputProps) {

  return (
    <TextInput
      value={value}
      onChange={onChange}
      defaultValue={defaultValue} 
      placeholder={placeholder}
      label={label}
      description={description}
      error={error}
      required={required}
      disabled={disabled}
      type={type}
      size={size}
      radius={radius}
      leftSection={leftSection}
      rightSection={rightSection}
      readOnly={readOnly}
      name={name}
      id={id}
      styles={{
        input: {
          borderColor: error ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-3)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
            boxShadow: `0 0 0 2px var(--mantine-color-brand-1)`,
          },
          '&:disabled': {
            backgroundColor: 'var(--mantine-color-gray-1)',
            color: 'var(--mantine-color-gray-6)',
          },
        },
        label: {
          color: 'var(--mantine-other-black)',
          fontWeight: 500,
        },
        description: {
          color: 'var(--mantine-color-neutral-6)',
        },
        error: {
          color: 'var(--mantine-color-red-6)',
        },
        ...styles,
      }}
      {...(className && { className })}
      {...inputProps}
    />
  );
}